import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Minus } from "lucide-react";
import { useUiPreferences } from "@/hooks/useUiPreferences";
import { useThemeConfig } from "@/components/ui/theme-config-provider";

export const SettingsSafeAreaSection: React.FC = () => {
  const { safeAreaTopOverride, safeAreaBottomOverride, updateSafeAreaTop, updateSafeAreaBottom, SAFE_AREA_LIMITS_TOP, SAFE_AREA_LIMITS_BOTTOM } = useUiPreferences();
  const { theme } = useThemeConfig();
  const [isLoading, setIsLoading] = useState(false);
  const [topInput, setTopInput] = useState<string>(safeAreaTopOverride?.toString() ?? "");
  const [bottomInput, setBottomInput] = useState<string>(safeAreaBottomOverride?.toString() ?? "");
  const [displayTop, setDisplayTop] = useState<number>(safeAreaTopOverride ?? 0);
  const [displayBottom, setDisplayBottom] = useState<number>(safeAreaBottomOverride ?? 0);

  const normalizeTopValue = (value: number | null) => {
    if (value === null) {
      return null;
    }
    const rounded = Math.round(value / SAFE_AREA_LIMITS_TOP.STEP) * SAFE_AREA_LIMITS_TOP.STEP;
    const clamped = Math.max(SAFE_AREA_LIMITS_TOP.MIN, Math.min(SAFE_AREA_LIMITS_TOP.MAX, rounded));
    return clamped;
  };

  const normalizeBottomValue = (value: number | null) => {
    if (value === null) {
      return null;
    }
    const rounded = Math.round(value / SAFE_AREA_LIMITS_BOTTOM.STEP) * SAFE_AREA_LIMITS_BOTTOM.STEP;
    const clamped = Math.max(SAFE_AREA_LIMITS_BOTTOM.MIN, Math.min(SAFE_AREA_LIMITS_BOTTOM.MAX, rounded));
    return clamped;
  };

  // Sincronizza gli input con i valori del backend
  useEffect(() => {
    setTopInput(safeAreaTopOverride?.toString() ?? "");
    setDisplayTop(safeAreaTopOverride ?? 0);
  }, [safeAreaTopOverride]);

  useEffect(() => {
    setBottomInput(safeAreaBottomOverride?.toString() ?? "");
    setDisplayBottom(safeAreaBottomOverride ?? 0);
  }, [safeAreaBottomOverride]);

  const handleTopChange = async (value: number | null) => {
    const normalized = normalizeTopValue(value);
    setIsLoading(true);
    const displayValue = normalized ?? 0;
    setDisplayTop(displayValue);
    setTopInput(normalized !== null ? normalized.toString() : "");
    try {
      await updateSafeAreaTop(normalized);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBottomChange = async (value: number | null) => {
    const normalized = normalizeBottomValue(value);
    setIsLoading(true);
    const displayValue = normalized ?? 0;
    setDisplayBottom(displayValue);
    setBottomInput(normalized !== null ? normalized.toString() : "");
    try {
      await updateSafeAreaBottom(normalized);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTopInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setTopInput(val);
  };

  const handleTopInputBlur = async () => {
    if (topInput === "") {
      await handleTopChange(null);
    } else {
      const num = parseInt(topInput, 10);
      if (!isNaN(num)) {
        await handleTopChange(num);
      } else {
        setTopInput(displayTop.toString());
      }
    }
  };

  const handleBottomInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setBottomInput(val);
  };

  const handleBottomInputBlur = async () => {
    if (bottomInput === "") {
      await handleBottomChange(null);
    } else {
      const num = parseInt(bottomInput, 10);
      if (!isNaN(num)) {
        await handleBottomChange(num);
      } else {
        setBottomInput(displayBottom.toString());
      }
    }
  };

  return (
    <div
      className="rounded-lg shadow p-6 mb-6"
      style={{
        borderRadius: theme.borderRadius || "0.5rem",
        backgroundColor: theme.cardBackgroundColor || theme.backgroundColor || "#ffffff",
        borderColor: theme.borderColor,
        border: `1px solid ${theme.borderColor || "#e5e7eb"}`,
      }}
    >
      {/* Header */}
      <h2
        className="text-xl font-semibold mb-2"
        style={{ color: theme.sectionTitleColor }}
      >
        Spazi Superiore e Inferiore
      </h2>
      <p
        className="text-sm mb-6"
        style={{ color: theme.sectionTextColor }}
      >
        Regola gli spazi vuoti in alto e in basso per adattarsi al tuo dispositivo
      </p>

      <div className="space-y-8">
        {/* Top Safe Area */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium" style={{ color: theme.sectionTextColor }}>
              Spazio Superiore
            </label>
            <span className="text-xs" style={{ color: theme.sectionTextColor }}>
              {safeAreaTopOverride === null ? "Auto" : `${displayTop}px`}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => handleTopChange(displayTop - SAFE_AREA_LIMITS_TOP.STEP)}
              disabled={isLoading || displayTop <= SAFE_AREA_LIMITS_TOP.MIN}
              style={{
                borderColor: theme.borderColor,
                color: theme.sectionTextColor,
              }}
            >
              <Minus className="h-4 w-4" />
            </Button>

            <Input
              type="number"
              min={SAFE_AREA_LIMITS_TOP.MIN}
              max={SAFE_AREA_LIMITS_TOP.MAX}
              step={SAFE_AREA_LIMITS_TOP.STEP}
              value={topInput}
              onChange={handleTopInputChange}
              onBlur={handleTopInputBlur}
              disabled={isLoading}
              className="text-center"
              placeholder="0"
              style={{
                borderColor: theme.borderColor,
                color: theme.sectionTextColor,
                backgroundColor: theme.inputBackgroundColor || theme.backgroundColor,
              }}
            />

            <Button
              variant="outline"
              size="icon"
              onClick={() => handleTopChange(displayTop + SAFE_AREA_LIMITS_TOP.STEP)}
              disabled={isLoading || displayTop >= SAFE_AREA_LIMITS_TOP.MAX}
              style={{
                borderColor: theme.borderColor,
                color: theme.sectionTextColor,
              }}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          <p className="text-xs" style={{ color: theme.sectionTextColor }}>
            Usa questo per lasciare spazio in alto per il notch o la barra di stato
          </p>
        </div>

        {/* Bottom Safe Area */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium" style={{ color: theme.sectionTextColor }}>
              Spazio Inferiore
            </label>
            <span className="text-xs" style={{ color: theme.sectionTextColor }}>
              {safeAreaBottomOverride === null ? "Auto" : `${displayBottom}px`}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => handleBottomChange(displayBottom - SAFE_AREA_LIMITS_BOTTOM.STEP)}
              disabled={isLoading || displayBottom <= SAFE_AREA_LIMITS_BOTTOM.MIN}
              style={{
                borderColor: theme.borderColor,
                color: theme.sectionTextColor,
              }}
            >
              <Minus className="h-4 w-4" />
            </Button>

            <Input
              type="number"
              min={SAFE_AREA_LIMITS_BOTTOM.MIN}
              max={SAFE_AREA_LIMITS_BOTTOM.MAX}
              step={SAFE_AREA_LIMITS_BOTTOM.STEP}
              value={bottomInput}
              onChange={handleBottomInputChange}
              onBlur={handleBottomInputBlur}
              disabled={isLoading}
              className="text-center"
              placeholder="0"
              style={{
                borderColor: theme.borderColor,
                color: theme.sectionTextColor,
                backgroundColor: theme.inputBackgroundColor || theme.backgroundColor,
              }}
            />

            <Button
              variant="outline"
              size="icon"
              onClick={() => handleBottomChange(displayBottom + SAFE_AREA_LIMITS_BOTTOM.STEP)}
              disabled={isLoading || displayBottom >= SAFE_AREA_LIMITS_BOTTOM.MAX}
              style={{
                borderColor: theme.borderColor,
                color: theme.sectionTextColor,
              }}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          <p className="text-xs" style={{ color: theme.sectionTextColor }}>
            Usa questo per lasciare spazio in basso per i gesti di navigazione
          </p>
        </div>

        {/* Info Box */}
        <div 
          className="text-xs space-y-1 pt-4 border-t"
          style={{ 
            borderColor: theme.borderColor,
            color: theme.sectionTextColor 
          }}
        >
          <p>💡 <strong>Come funziona:</strong> Aumenta il valore se i contenuti sono coperti, diminuiscilo se c'è troppo spazio vuoto</p>
          <p>• Usa i pulsanti ± per muoverti di 4px o digita direttamente il numero</p>
          <p>• "Spazio inferiore" sposta pulsanti e liste sopra la barra gestuale</p>
        </div>
      </div>
    </div>
  );
};
